import { TableContext } from "../context";
import React, { useState, useContext, useEffect, useMemo } from "react";
import "../App.css";
import Papa from "papaparse";
import { DateTime } from 'luxon';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LaunchIcon from '@mui/icons-material/Launch';
import {
    McTable,
    McButton,
    McPagination,
    McTooltip,
    McIcon
} from "@maersk-global/mds-react-wrapper";
import FilterButton from "./FilterButton";
 
/**
 * VendorChangeTable component for displaying and filtering vendor changes data.
 * Provides options to filter data by severity, status, region, and other fields.
 * Allows downloading the filtered data as a CSV file.
 *
 * @param {Object} props - The component props.
 * @param {string} props.Header1 - The first header for the table.
 * @param {string} props.Header2 - The second header for the table.
 * @param {string} props.Header3 - The third header for the table.
 * @param {string} props.Header4 - The fourth header for the table.
 * @param {string} props.Header5 - The fifth header for the table.
 * @param {string} props.Header6 - The sixth header for the table.
 * @param {string} props.Header7 - The seventh header for the table.
 */
export default function VendorChangeTable(props) {
    const { vendorChangeTableData, selectedTime, search, searchType } = useContext(TableContext);
    const [headingText, setHeadingText] = useState('Ongoing Changes');
    const [activeFilters, setActiveFilters] = useState([]);
    const [tableStyles, setTableStyles] = useState();
 
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 100; // Number of records per page
 
    // Table state preservation
    const [sortColumn, setSortColumn] = useState("changeId");
    const [sortDirection, setSortDirection] = useState("descending");
    const [tableScrollPosition, setTableScrollPosition] = useState(0);
 
    // Define table columns for the filter system and McTable
    const tableColumns = [
        { id: "changeId", label: props.Header1 || "Change ID", sortable: true },
        { id: "affectedAccounts", label: "Affected Accounts", sortable: true, nowrap: false },
        { id: "applicationId", label: "Application ID", sortable: true },
        { id: "applicationName", label: "Application Name", sortable: true },
        { id: "changeTitle", label: props.Header2 || "Change Title", sortable: true, nowrap: false },
        { id: "category", label: props.Header3 || "Category", sortable: true },
        { id: "serviceName", label: props.Header4 || "Service Impacted", sortable: true },
        { id: "region", label: props.Header5 || "Region", sortable: true },
        { id: "severity", label: props.Header6 || "Severity", sortable: true },
        { id: "stage", label: "Stage", sortable: true },
        { id: "scheduledStartTime", label: "Maintenance Start Date", sortable: true },
        { id: "scheduledEndTime", label: "Maintenance End Date", sortable: true },
        { id: "duration", label: props.Header7 || "Duration", sortable: true }
    ];
 
    // Filter columns for the advanced filter (using original field names)
    const filterColumns = [
        { id: "Change ID", label: "Change ID" },
        { id: "Affected Accounts", label: "Affected Accounts" },
        { id: "Change Title", label: "Change Title" },
        { id: "Category", label: "Category" },
        { id: "Service Name", label: "Service Name" },
        { id: "Region", label: "Region" },
        { id: "Severity", label: "Severity" },
        { id: "Scheduled Start Time", label: "Scheduled Start Time" },
        { id: "Scheduled End Time", label: "Scheduled End Time" },
        { id: "Duration", label: "Duration" }
    ];
 
    const sortArrayByProperty = (data, property, order = 'ascending') => {
        return data.sort((a, b) => {
            let comparison = 0;
 
            if (property === "scheduledStartTime" || property === "scheduledEndTime") {
                const dateA = DateTime.fromFormat(a[property], 'dd-MM-yyyy HH:mm:ss');
                const dateB = DateTime.fromFormat(b[property], 'dd-MM-yyyy HH:mm:ss');
                if (dateA < dateB) comparison = -1;
                else if (dateA > dateB) comparison = 1;
            }
            else {
                if (a[property] > b[property]) {
                    comparison = 1;
                } else if (a[property] < b[property]) {
                    comparison = -1;
                }
            }
            return order === 'ascending' ? comparison : comparison * -1;
        });
    }
 
    // Apply advanced filters from the filter menu
    const applyAdvancedFilters = (data, filters) => {
        if (!filters || filters.length === 0) return data;
 
        return data.filter((item) => {
            return filters.every((filter) => {
                let itemValue = item[filter.field];
                const filterValue = filter.value.toLowerCase();
 
                if (itemValue === undefined || itemValue === null) return false;
 
                if (filter.field === "Scheduled Start Time" || filter.field === "Scheduled End Time") {
                    const dt = DateTime.fromFormat(itemValue, "yyyy-MM-dd HH:mm:ss");
                    itemValue = dt.isValid ? dt.toFormat("dd-MM-yyyy HH:mm:ss") : "";
                }
 
                const itemString = String(itemValue).toLowerCase();
 
                switch (filter.operator) {
                    case "contains":
                        return itemString.includes(filterValue);
                    case "Does not contain":
                        return !itemString.includes(filterValue);
                    case "equals":
                        return itemString === filterValue;
                    case "Is not":
                        return itemString !== filterValue;
                    case "startsWith":
                        return itemString.startsWith(filterValue);
                    case "endsWith":
                        return itemString.endsWith(filterValue);
                    default:
                        return true;
                }
            });
        });
    };
 
    const filteredData = useMemo(() => {
        if (!vendorChangeTableData) return [];
 
        // Apply advanced filters
        let filtered = applyAdvancedFilters(vendorChangeTableData, activeFilters);
 
        // Apply global/freelook search if active
        if (search && searchType === 'global') {
            const searchTerms = search.toLowerCase().split(',').map(term => term.trim()).filter(term => term);
 
            filtered = filtered.filter(item => {
                // Create a searchable string from all relevant fields
                const searchableContent = [
                    item["Change ID"],
                    item["Change Title"],
                    item["Category"],
                    item["Service Name"],
                    item["Region"],
                    item["Severity"],
                    item["Stage"],
                    item["Affected Accounts"],
                    item["Application ID"],
                    item["Application Name"],
                    item["Scheduled Start Time"],
                    item["Scheduled End Time"],
                    item["Duration"],
                    item["Impact Summary"],
                    item["Notification Source"],
                    item["Resource Name"] || "",
                    item["All Stages"] || "",
                ].join(' ').toLowerCase();
 
                // Check if any search term matches
                return searchTerms.some(term => searchableContent.includes(term));
            });
        }
 
        // Sort by start time (most recent first)
        filtered.sort((a, b) => {
            const dateA = DateTime.fromFormat(a["Scheduled Start Time"], 'yyyy-MM-dd HH:mm:ss');
            const dateB = DateTime.fromFormat(b["Scheduled Start Time"], 'yyyy-MM-dd HH:mm:ss');
            if (dateA < dateB) return 1;
            if (dateA > dateB) return -1;
            return 0;
        });
 
        return filtered;
    }, [vendorChangeTableData, activeFilters, search, searchType]);
 
    // Client-side pagination - slice data before transformation
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);
 
    const transformedData = useMemo(() => {
        let dataToSort = paginatedData.map((item, index) => {
            return {
                id: `row-${index}`,
                uniqueId: item["Unique ID"],
                changeId: item["Change ID"],
                affectedAccounts: item["Affected Accounts"],
                changeTitle: item["Change Title"],
                category: item["Category"],
                serviceName: item["Service Name"],
                region: item["Region"],
                severity: item["Severity"],
                stage: item["Stage"],
                applicationId: item["Application ID"] || "",
                applicationName: item["Application Name"] || "",
                scheduledStartTime: item["Scheduled Start Time"],
                scheduledEndTime: item["Scheduled End Time"],
                duration: item["Duration"],
                _originalData: item
            };
        });
 
        if (sortColumn) {
            dataToSort = sortArrayByProperty([...dataToSort], sortColumn, sortDirection);
        }
 
        return dataToSort;
    }, [paginatedData, sortColumn, sortDirection]);
 
    const handlePageChange = (event) => {
        setCurrentPage(event.detail);
    };
 
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);
 
    useEffect(() => {
        if (tableScrollPosition > 0) {
            const tableElement = document.querySelector('mc-table');
            if (tableElement) {
                const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                    tableElement.querySelector('.table-container');
                if (scrollContainer) {
                    scrollContainer.scrollTop = tableScrollPosition;
                }
            }
        }
    }, [transformedData]);
 
    useEffect(() => {
        setTableStyles(`
            .mds-table {
                max-height: 65vh !important;
            }
        `);
    }, [transformedData]);
 
    useEffect(() => {
        const now = DateTime.now();
        const start = DateTime.fromJSDate(selectedTime.start);
        const end = DateTime.fromJSDate(selectedTime.end);
 
        const isPast = start.year < now.year ||
            (start.year === now.year && start.month < now.month) ||
            (start.year === now.year && start.month === now.month && start.day < now.day) ||
            (start.year === now.year && start.month === now.month && start.day === now.day && start.hour < now.hour) ||
            (start.year === now.year && start.month === now.month && start.day === now.day && start.hour === now.hour && start.minute < now.minute);
 
        const isFuture = end.year > now.year ||
            (end.year === now.year && end.month > now.month) ||
            (end.year === now.year && end.month === now.month && end.day > now.day) ||
            (end.year === now.year && end.month === now.month && end.day === now.day && end.hour > now.hour) ||
            (end.year === now.year && end.month === now.month && end.day === now.day && end.hour === now.hour && end.minute > now.minute);
 
        if (isPast && !isFuture) {
            setHeadingText("Past Changes");
        } else if (isFuture && !isPast) {
            setHeadingText("Future Changes");
        } else {
            setHeadingText("Ongoing Changes");
        }
    }, [selectedTime]);
 
    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical':
            case 'high':
                return '#e75858';
            case 'medium':
                return '#d8d52c';
            case 'low':
                return '#00786b';
            default:
                return 'black';
        }
    };
 
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'resolved':
                return '#00786b';
            case 'in progress':
            case 'active':
                return '#0077b2';
            case 'scheduled':
                return '#d8d52c';
            case 'cancelled':
                return '#666666';
            default:
                return 'black';
        }
    };
 
    const handleDownloadClick = () => {
        const headers = [
            "Change ID",
            props.Header1 || "Maintenance/ Change Title",
            "Category",
            props.Header2 || "Service Name",
            props.Header3 || "Region",
            "Severity",
            "Status",
            props.Header5 || "Scheduled Start Time",
            "Scheduled End Time",
            "Duration",
            props.Header6 || "Impacted Resources",
            "Affected Accounts",
            props.Header7 || "Service Health Links",
        ];
        const csvData = filteredData.map(item => [
            item["Change ID"],
            item["Maintenance/ Change Title"],
            item["Category"],
            item["Service Name"],
            item["Region"],
            item["Severity"],
            item["Status"],
            item["Scheduled Start Time"],
            item["Scheduled End Time"],
            item["Duration"],
            item["Impacted Resources"],
            item["Affected Accounts"],
            item["Service Health Links"]
        ]);
        const csv = Papa.unparse({
            fields: headers,
            data: csvData,
        });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const fileName = `Vendor_Changes_${DateTime.now().toFormat('yyyy-MM-dd_HH-mm-ss')}.csv`;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };
 
    const handleFilterApply = (filters) => {
        setActiveFilters(filters);
    };
 
    const handleTableSorted = ({ detail: { column, direction } }) => {
        setSortColumn(column);
        setSortDirection(direction);
 
        // Preserve scroll position after sorting
        setTimeout(() => {
            const tableElement = document.querySelector('mc-table');
            if (tableElement && tableScrollPosition > 0) {
                const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                    tableElement.querySelector('.table-container');
                if (scrollContainer) {
                    scrollContainer.scrollTop = tableScrollPosition;
                }
            }
        }, 100);
    }
 
    // Save scroll position before interactions
    const saveScrollPosition = () => {
        const tableElement = document.querySelector('mc-table');
        if (tableElement) {
            const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                tableElement.querySelector('.table-container');
            if (scrollContainer) {
                setTableScrollPosition(scrollContainer.scrollTop);
            }
        }
    };
 
    const handleServiceHealthLinkClick = (url) => {
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };
 
    return (
        <div className="home-table-container" style={{ marginTop: "10px" }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    position: 'relative',
                }}
            >
                <div style={{ flex: '0 0 auto' }}>
                    <FilterButton
                        columns={filterColumns}
                        data={vendorChangeTableData || []}
                        onApply={handleFilterApply}
                        activeFilters={activeFilters}
                        setActiveFilters={setActiveFilters}
                    />
                </div>
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        textAlign: 'center',
                        width: 'max-content',
                        pointerEvents: 'none',
                    }}
                >
                    <span style={{ fontWeight: "600", fontSize: '1.2rem', color: "#555" }}>
                        {headingText}
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: '0 0 auto' }}>
                    {!props.isLoading &&
                        <span style={{ fontSize: '1rem', color: '#0077b2' }}>
                            Total: {filteredData.length}
                        </span>
                    }
                    <McButton
                        label="Download"
                        appearance="primary"
                        variant="filled"
                        fit="small"
                        click={handleDownloadClick}
                    >
                        <span slot="trailingicon">
                            <mc-icon class="my-icon" icon="tray-arrow-down" color="white"></mc-icon>
                        </span>
                    </McButton>
                </div>
            </div>
            <div>
                <McTable
                    data={transformedData}
                    columns={tableColumns}
                    datakey="id"
                    headersticky
                    fit="small"
                    sortmanual
                    sortchange={handleTableSorted}
                    sortdefaultcolumnid={sortColumn}
                    sortdefaultdirection={sortDirection}
                    customstyles={tableStyles}
                    resetscrollonpagechange={false}
                    stateslotheight="200px"
                >
                    {props.isLoading ? (
                        <mc-loading-indicator slot="state"></mc-loading-indicator>
                    ) : (
                        transformedData?.length === 0 && (
                            <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.5rem", fontWeight: "600" }}>
                                No Data Found
                            </div>
                        )
                    )}
                    {transformedData?.map((row) => (
                        <React.Fragment key={row.id}>
                            {/* Change ID Cell - Clickable */}
                            <div slot={`${row.id}_changeId`}>
                                <span
                                    onClick={() => {
                                        props.setisdrawerOpen(true);
                                        props.setDrawerType("vendor_change_details");
                                        props.setDrawerChange(row.uniqueId);
                                    }}
                                    style={{
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        color: '#0077b2',
                                        cursor: 'pointer'
                                    }}>
                                    {row.changeId}
                                </span>
                            </div>
 
                            {/* Affected Accounts Cell */}
                            <div slot={`${row.id}_affectedAccounts`}>
                                <div>
                                    <span style={{ maxWidth: "300px", wordBreak: "break-word", fontSize: "14px" }}>
                                        {row.affectedAccounts}
                                    </span>
                                    {row._originalData["Resource Name"] && (
                                        <div style={{ fontSize: '0.75rem', color: '#666666', marginTop: '4px' }}>
                                            Resource Name: {row._originalData["Resource Name"]}
                                        </div>
                                    )}
                                </div>
                            </div>
 
                            {/* Application ID Cell */}
                            <div slot={`${row.id}_applicationId`}>
                                <span style={{ fontSize: '0.9rem' }}>
                                    {row.applicationId || "N/A"}
                                </span>
                            </div>
 
                            {/* Application Name Cell */}
                            <div slot={`${row.id}_applicationName`}>
                                <span style={{ maxWidth: "300px", wordBreak: "break-word", fontSize: '0.9rem' }}>
                                    {row.applicationName || "N/A"}
                                </span>
                            </div>
 
                            {/* Change Title Cell */}
                            <div slot={`${row.id}_changeTitle`}>
                                <span style={{ maxWidth: "400px", wordBreak: "break-word", fontSize: "14px" }}>
                                    {row.changeTitle}
                                </span>
                            </div>
 
                            {/* Category Cell */}
                            <div slot={`${row.id}_category`}>
                                <span style={{
                                    backgroundColor: '#f0f0f0',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem'
                                }}>
                                    {row.category}
                                </span>
                            </div>
 
                            {/* Service Name Cell */}
                            <div slot={`${row.id}_serviceName`}>
                                <span style={{ fontSize: '0.9rem' }}>
                                    {row.serviceName}
                                </span>
                            </div>
 
                            {/* Region Cell */}
                            <div slot={`${row.id}_region`}>
                                <span style={{
                                    backgroundColor: '#e3f2fd',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem',
                                    display: 'inline-block',
                                    textAlign: 'center',
                                    minWidth: '60px',
                                    maxWidth: '120px',
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    lineHeight: '1.3',
                                    whiteSpace: 'normal',
                                    verticalAlign: 'middle'
                                }}>
                                    {row.region}
                                </span>
                            </div>
 
                            {/* Severity Cell */}
                            <div slot={`${row.id}_severity`}>
                                <span style={{
                                    color: getSeverityColor(row.severity),
                                    fontWeight: "500",
                                    fontSize: '0.9rem'
                                }}>
                                    {row.severity}
                                </span>
                            </div>
 
                            {/* Stage Cell */}
                            <div slot={`${row.id}_stage`}>
                                <span style={{
                                    backgroundColor: '#ffd3d3ff',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem'
                                }}>
                                    {row.stage}
                                </span>
                            </div>
 
                            {/* Scheduled Start Time Cell */}
                            <div slot={`${row.id}_scheduledStartTime`}>
                                <span style={{ fontSize: '0.85rem' }}>
                                    {row.scheduledStartTime}
                                </span>
                            </div>
 
                            {/* Scheduled End Time Cell */}
                            <div slot={`${row.id}_scheduledEndTime`}>
                                <span style={{ fontSize: '0.85rem' }}>
                                    {row.scheduledEndTime}
                                </span>
                            </div>
 
                            {/* Duration Cell */}
                            <div slot={`${row.id}_duration`}>
                                <span style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    {row.duration}
                                    <McTooltip
                                        opendelay={50}
                                        appearance="neutral-default"
                                        trigger="hover focus"
                                    >
                                        <McIcon slot="trigger" icon="info-circle" style={{ color: '#0077b2', cursor: 'pointer', fontSize: '1rem' }}></McIcon>
                                        <span>Maintenance can occur on any day in between the start and end date.</span>
                                    </McTooltip>
                                </span>
                            </div>
                        </React.Fragment>
                    ))}
                </McTable>
 
                {/* Pagination Component */}
                {!props.isLoading && filteredData.length > recordsPerPage && (
                    <div style={{ paddingTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                        <McPagination
                            currentpage={currentPage}
                            totalpages={totalPages}
                            pagechange={handlePageChange}
                        />
                    </div>
                )}
            </div>
            <div style={{ marginTop: "8vh", visibility: "hidden" }}>
                ""
            </div>
        </div>
    );
}